import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsapp from "@/components/FloatingWhatsapp";
import MobileBottomNav from "@/components/MobileBottomNav";
import { useSeo } from "@/lib/seo";

/**
 * Autoescuelas Faro — Artículo de blog
 * Ruta: /blog/cambios-dgt-1-octubre-2026
 *
 * NOTA PARA EMERGENT:
 * - Las etiquetas <title>, <meta> y <link rel="canonical"> deben gestionarse con
 *   EL MISMO mecanismo que ya usa el resto del sitio (react-helmet-async o el
 *   componente SEO propio). NO duplicar aquí.
 * - El JSON-LD se inyecta desde useEffect y se limpia al desmontar.
 */

const NAVY = "#161692";
const RED = "#DE0000";

const PUBLISHED = "2026-08-22";
const MODIFIED = "2026-08-22";
const URL = "https://autoescuelasfaro.com/blog/cambios-dgt-1-octubre-2026";

const FAQS = [
  {
    q: "¿Cuándo entran en vigor las nuevas normas de la DGT?",
    a: "El 1 de octubre de 2026, sin periodo de adaptación. Dos medidas concretas se retrasan hasta el 1 de octubre de 2027: el casco homologado en ciclomotores y el alumbrado diurno en patinetes.",
  },
  {
    q: "¿A qué velocidad puedo adelantar a un ciclista?",
    a: "Fuera de poblado, al menos 20 km/h por debajo del límite de la vía, manteniendo 1,5 metros de separación lateral. En una vía limitada a 90 km/h, a 70 km/h como máximo. Si la calzada tiene más de un carril por sentido, además hay que cambiar completamente de carril.",
  },
  {
    q: "¿Es obligatorio el casco en patinete eléctrico?",
    a: "Sí. Desde el 1 de octubre de 2026 todos los conductores de vehículos de movilidad personal deben usar casco homologado o certificado, correctamente abrochado, en cualquier tipo de vía.",
  },
  {
    q: "¿Qué edad hay que tener para conducir un patinete eléctrico?",
    a: "15 años. Es la edad mínima que fija el artículo 155.3 del Reglamento General de Circulación.",
  },
  {
    q: "¿Pueden las motos circular por el arcén cuando hay atasco?",
    a: "Solo en tramos previamente acordados y señalizados con señal vertical fija y marcas viales, a un máximo de 30 km/h, en columna de a uno y únicamente cuando la circulación de los carriles esté detenida por congestión.",
  },
  {
    q: "¿Los profesores de autoescuela tienen que llevar cinturón?",
    a: "Sí. La reforma suprime la exención que amparaba a quien va a cargo de los mandos adicionales durante el aprendizaje de la conducción o las pruebas de aptitud.",
  },
  {
    q: "¿Estas normas entran en el examen teórico?",
    a: "Sí. Los cambios del Reglamento General de Circulación se trasladan al banco de preguntas de la DGT. Conviene estudiar con material actualizado a partir de octubre de 2026.",
  },
];

const TOC = [
  ["usuario-vulnerable", "Qué es un «usuario vulnerable»"],
  ["ciclistas", "Adelantar a un ciclista"],
  ["averiados", "Vehículos averiados"],
  ["carril-emergencia", "Carril de emergencia"],
  ["nieve", "Nieve en autovía"],
  ["motos", "Motos y ciclomotores"],
  ["bicicletas", "Bicicletas"],
  ["patinetes", "Patinetes y VMP"],
  ["peatones", "Peatones y ciudad"],
  ["cinturon", "Cinturón de seguridad"],
  ["autocaravanas", "Autocaravanas"],
  ["bulos", "Cuatro bulos"],
  ["examen", "Cómo afecta al examen"],
  ["fechas", "Fechas clave"],
  ["faq", "Preguntas frecuentes"],
];

function useJsonLd(id, data) {
  useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = id;
    el.text = JSON.stringify(data);
    document.head.appendChild(el);
    return () => {
      const prev = document.getElementById(id);
      if (prev) prev.remove();
    };
  }, [id, data]);
}

/* ---------- Piezas de UI ---------- */

const H2 = ({ id, children }) => (
  <h2
    id={id}
    className="scroll-mt-28 mt-14 mb-4 text-2xl md:text-3xl font-bold leading-tight"
    style={{ color: NAVY }}
  >
    {children}
  </h2>
);

const H3 = ({ children }) => (
  <h3 className="mt-8 mb-3 text-lg md:text-xl font-semibold text-slate-900">
    {children}
  </h3>
);

const P = ({ children }) => (
  <p className="mb-4 text-[17px] leading-8 text-slate-700">{children}</p>
);

const UL = ({ children }) => (
  <ul className="mb-5 space-y-2 text-[17px] leading-8 text-slate-700">
    {children}
  </ul>
);

const LI = ({ children }) => (
  <li className="relative pl-6">
    <span
      className="absolute left-0 top-[14px] h-[6px] w-[6px] rounded-full"
      style={{ backgroundColor: RED }}
      aria-hidden="true"
    />
    {children}
  </li>
);

/** Referencia al artículo del Reglamento */
const Art = ({ children }) => (
  <span className="whitespace-nowrap rounded bg-slate-100 px-1.5 py-0.5 text-[13px] font-semibold tracking-wide text-slate-600">
    {children}
  </span>
);

/** Aviso destacado */
const Callout = ({ tone = "navy", title, children }) => {
  const color = tone === "red" ? RED : NAVY;
  return (
    <div
      className="my-8 border-l-4 bg-slate-50 px-5 py-4 rounded-r-lg"
      style={{ borderColor: color }}
    >
      {title && (
        <p className="mb-1 text-sm font-bold uppercase tracking-wider" style={{ color }}>
          {title}
        </p>
      )}
      <div className="text-[16px] leading-7 text-slate-700">{children}</div>
    </div>
  );
};

/* ---------- Componente principal ---------- */

function CambiosDGTArticle() {
  useJsonLd("ld-article", {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "Todos los cambios de la DGT que entran en vigor el 1 de octubre de 2026",
    description:
      "El Real Decreto 518/2026 modifica el Reglamento de Circulación desde el 1 de octubre: adelantar a ciclistas, motos en el arcén, casco en patinetes, cinturón y más.",
    inLanguage: "es-ES",
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
    mainEntityOfPage: { "@type": "WebPage", "@id": URL },
    image: ["https://autoescuelasfaro.com/assets/blog/cambios-dgt-octubre-2026.webp"],
    author: {
      "@type": "Organization",
      name: "Autoescuelas Faro",
      url: "https://autoescuelasfaro.com",
    },
    publisher: {
      "@type": "Organization",
      name: "Autoescuelas Faro",
      logo: {
        "@type": "ImageObject",
        url: "https://autoescuelasfaro.com/assets/logo-autoescuelas-faro.webp",
      },
    },
    citation: {
      "@type": "Legislation",
      name: "Real Decreto 518/2026, de 24 de junio",
      url: "https://www.boe.es/eli/es/rd/2026/06/24/518",
    },
  });

  useJsonLd("ld-faq", {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  });

  useJsonLd("ld-breadcrumb", {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Inicio", item: "https://autoescuelasfaro.com/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://autoescuelasfaro.com/blog" },
      { "@type": "ListItem", position: 3, name: "Cambios DGT 1 de octubre de 2026", item: URL },
    ],
  });

  return (
    <article className="bg-white">
      {/* HERO */}
      <header className="border-b border-slate-200">
        <div className="mx-auto max-w-3xl px-5 pt-10 pb-8 md:pt-16">
          <nav aria-label="Migas de pan" className="mb-6 text-sm text-slate-500">
            <Link to="/" className="hover:underline">Inicio</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:underline">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">Cambios DGT octubre 2026</span>
          </nav>

          <p
            className="mb-3 text-xs font-bold uppercase tracking-[0.18em]"
            style={{ color: RED }}
          >
            Normativa · Real Decreto 518/2026
          </p>

          <h1
            className="text-3xl font-extrabold leading-[1.15] md:text-[2.75rem]"
            style={{ color: NAVY }}
          >
            Todos los cambios de la DGT que entran en vigor el 1 de octubre de 2026
          </h1>

          <p className="mt-5 text-lg leading-8 text-slate-600">
            Hemos leído el Real Decreto entero en el BOE, artículo por artículo. Esto
            es lo que dice de verdad, y estos son los cuatro bulos que circulan por ahí.
          </p>

          <p className="mt-6 text-sm text-slate-500">
            Publicado el 22 de agosto de 2026 · Equipo docente de Autoescuelas Faro ·
            Lectura: 9 min
          </p>
        </div>
      </header>

      {/* ÍNDICE */}
      <div className="mx-auto max-w-3xl px-5">
        <nav
          aria-label="Índice del artículo"
          className="my-10 rounded-xl border border-slate-200 p-5"
        >
          <p className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-500">
            En este artículo
          </p>
          <ol className="grid gap-x-6 gap-y-2 text-[15px] sm:grid-cols-2">
            {TOC.map(([id, label], i) => (
              <li key={id} className="flex gap-2">
                <span className="tabular-nums text-slate-400">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <a href={`#${id}`} className="text-slate-700 hover:underline">
                  {label}
                </a>
              </li>
            ))}
          </ol>
        </nav>
      </div>

      {/* CUERPO */}
      <div className="mx-auto max-w-3xl px-5 pb-4">
        <P>
          El 1 de octubre de 2026 entra en vigor la mayor reforma del Reglamento
          General de Circulación desde 2003. La aprobó el Consejo de Ministros el 23 de
          junio y se publicó dos días después en el BOE como{" "}
          <a
            href="https://www.boe.es/eli/es/rd/2026/06/24/518"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline decoration-2 underline-offset-2"
            style={{ color: NAVY }}
          >
            Real Decreto 518/2026, de 24 de junio
          </a>
          , «en materia de protección a usuarios vulnerables de la vía».
        </P>
        <P>
          En estas semanas se han publicado decenas de titulares sobre el tema y muchos
          contienen errores. Hemos leído el texto completo del Real Decreto y esto es lo
          que dice, con el número de artículo del Reglamento en cada apartado para que
          puedas comprobarlo tú mismo.
        </P>

        {/* TABLA RESUMEN */}
        <div className="my-10 overflow-x-auto rounded-xl border border-slate-200">
          <table className="w-full min-w-[560px] border-collapse text-left text-[15px]">
            <caption className="sr-only">
              Resumen de los cambios del Real Decreto 518/2026 y su fecha de aplicación
            </caption>
            <thead>
              <tr style={{ backgroundColor: NAVY }}>
                <th scope="col" className="px-4 py-3 font-semibold text-white">Novedad</th>
                <th scope="col" className="px-4 py-3 font-semibold text-white">Artículo</th>
                <th scope="col" className="px-4 py-3 font-semibold text-white">Desde</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-slate-700">
              {[
                ["Adelantar a ciclistas: 1,5 m + reducir 20 km/h fuera de poblado", "85.4", "1 oct 2026"],
                ["Rebasar un vehículo averiado: 1,5 m + reducir 20 km/h", "88.1", "1 oct 2026"],
                ["Carril de emergencia en atascos de autopista y autovía", "32.1", "1 oct 2026"],
                ["Nieve en autovía: prohibido adelantar", "32.2", "1 oct 2026"],
                ["Motos: guantes en vía interurbana y calzado cerrado siempre", "118.1", "1 oct 2026"],
                ["Motos por el arcén en atascos (tramos señalizados)", "36.3", "1 oct 2026"],
                ["Patinetes: 15 años mínimo y casco obligatorio", "155.3 / 118.3", "1 oct 2026"],
                ["Bicis en ciudad: centro del carril y 5 m de distancia", "153", "1 oct 2026"],
                ["Fin de exenciones del cinturón (taxi, reparto, autoescuela)", "119.2", "1 oct 2026"],
                ["Estacionamiento de autocaravanas", "92.4", "1 oct 2026"],
                ["Casco homologado en ciclomotores", "D. T. 1.ª", "1 oct 2027"],
                ["Alumbrado diurno obligatorio en patinetes", "D. T. 3.ª", "1 oct 2027"],
              ].map(([a, b, c]) => (
                <tr key={a} className="align-top">
                  <td className="px-4 py-3">{a}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-slate-500">{b}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium">{c}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 1 */}
        <H2 id="usuario-vulnerable">Por primera vez existe una definición legal de «usuario vulnerable»</H2>
        <P>
          Es el cambio de fondo del que cuelga todo lo demás. El Real Decreto añade al
          anexo I de la Ley de Tráfico el concepto 86, que define como usuario vulnerable
          a quien, por el medio de desplazamiento que utiliza o por la edad, tiene más
          riesgo de sufrir lesiones en un accidente o de que estas sean más graves.
        </P>
        <P>
          Incluye a peatones —y dentro de ellos, de forma expresa, a personas con
          discapacidad, menores, personas mayores y personas con movilidad reducida—, a
          ciclistas, a conductores de vehículos de movilidad personal y a conductores y
          pasajeros de motocicletas y ciclomotores.
        </P>

        {/* 2 */}
        <H2 id="ciclistas">Adelantar a un ciclista cambia (y no solo a ciclistas)</H2>
        <P>
          El nuevo <Art>art. 85.4</Art> es el cambio que más afecta al día a día de
          cualquier conductor. Se aplica al adelantar a peatones, animales, vehículos de
          tracción animal, ciclomotores, vehículos de movilidad personal, ciclistas o un
          grupo de ciclistas. Tres obligaciones:
        </P>
        <UL>
          <LI>
            <strong>Separación lateral de al menos 1,5 metros</strong>, ocupando parte o
            la totalidad del carril contiguo o del contrario.
          </LI>
          <LI>
            <strong>Si la calzada tiene más de un carril por sentido, el cambio completo
            de carril es obligatorio.</strong> Ya no vale adelantar dentro del mismo
            carril dejando 1,5 metros.
          </LI>
          <LI>
            <strong>Fuera de poblado, reducir la velocidad al menos 20 km/h respecto al
            límite de la vía</strong>, antes de iniciar la maniobra y durante toda su
            ejecución.
          </LI>
        </UL>
        <P>
          Ese tercer punto es el que más cuesta interiorizar, porque invierte la lógica
          que se enseñaba hasta ahora. Durante años la idea era acortar la maniobra:
          adelanta rápido y sal cuanto antes del carril contrario. El preámbulo del Real
          Decreto dice justo lo contrario: la diferencia de velocidad entre el que
          adelanta y el adelantado ya es enorme, así que proteger al más débil obliga a
          reducir, no a aumentar.
        </P>
        <Callout tone="navy" title="Ejemplo">
          En una carretera convencional limitada a <strong>90 km/h</strong>, el
          adelantamiento se hace a <strong>70 km/h</strong> como máximo. Si el tramo está
          limitado a 100, a 80. Se restan 20 km/h <strong>al límite de la vía</strong>,
          no a la velocidad a la que tú ibas.
        </Callout>
        <P>
          El artículo añade además que <strong>un grupo de ciclistas se considera una
          única unidad móvil</strong>. Se adelanta al grupo entero de una vez.
        </P>

        {/* 3 */}
        <H2 id="averiados">Rebasar un vehículo averiado: la misma regla</H2>
        <P>
          El <Art>art. 88</Art> pasa a exigir lo mismo cuando te encuentras un vehículo
          inmovilizado que ocupa total o parcialmente tu carril:{" "}
          <strong>1,5 metros de anchura de seguridad y reducción de al menos 20 km/h
          respecto al límite de la vía</strong>.
        </P>
        <P>
          No se aplica solo a averías y accidentes: también cubre vehículos que prestan
          un servicio de urgencia, auxilio en carretera, mantenimiento de la vía o
          funciones de regulación y gestión del tráfico. Grúas, ambulancias, conservación
          de carreteras y agentes. El propio artículo califica estas infracciones como{" "}
          <strong>graves</strong>.
        </P>

        {/* 4 */}
        <H2 id="carril-emergencia">El carril de emergencia llega a España</H2>
        <P>
          El <Art>art. 32.1</Art> introduce una figura que existe desde hace años en
          Alemania y que aquí no estaba regulada. Cuando en autopista o autovía haya una
          retención y los vehículos circulen a paso de peatón o se detengan, hay que
          orillarse para dejar un espacio libre por el que puedan pasar los vehículos
          prioritarios. Y no se forma donde la mayoría supone:
        </P>
        <UL>
          <LI>
            <strong>Vías de dos carriles por sentido:</strong> el pasillo se forma{" "}
            <strong>en el centro</strong>, entre los dos carriles.
          </LI>
          <LI>
            <strong>Vías de tres o más carriles:</strong> se forma{" "}
            <strong>entre el carril de más a la izquierda y el contiguo</strong>. Los del
            carril izquierdo se pegan todo lo posible a la izquierda; todos los demás, a
            la derecha.
          </LI>
        </UL>
        <Callout tone="red" title="Ojo">
          No se deja libre el arcén ni el carril derecho. El pasillo de emergencia va por
          la izquierda.
        </Callout>

        {/* 5 */}
        <H2 id="nieve">Nieve en autopista y autovía: prohibido adelantar</H2>
        <P>
          El <Art>art. 32.2</Art> regula qué hacer cuando la nieve dificulte la
          circulación en autopistas y autovías: <strong>queda prohibido adelantar</strong>{" "}
          y es obligatorio circular por el carril de más a la derecha. En tramos de tres o
          más carriles se puede usar también el contiguo, pero el resto de carriles de la
          izquierda deben quedar expeditos para vehículos de emergencia y quitanieves.
        </P>

        {/* 6 */}
        <H2 id="motos">Motos y ciclomotores: equipamiento obligatorio</H2>
        <P>
          El <Art>art. 118.1</Art> reescribe qué hay que llevar puesto. Afecta a
          motocicletas, motos con sidecar, vehículos de tres ruedas, cuadriciclos,
          ciclomotores y quads:
        </P>
        <UL>
          <LI>
            <strong>Casco homologado conforme al Reglamento UNECE R22</strong>,
            correctamente abrochado, en todo tipo de vías.
          </LI>
          <LI>
            <strong>Guantes de protección</strong> cuando se circule por{" "}
            <strong>vías interurbanas</strong>.
          </LI>
          <LI>
            <strong>Calzado cerrado que cubra todo el pie</strong> en{" "}
            <strong>todo tipo de vías</strong>. Se acabaron las chanclas.
          </LI>
        </UL>
        <Callout tone="red" title="No es cierto">
          El Real Decreto <strong>no obliga a usar casco integral</strong>. Exige que esté
          homologado según R22, sin distinguir entre integral, modular o jet.
        </Callout>
        <P>
          Sí hay un cambio real en ciclomotores: hasta ahora bastaba con un casco{" "}
          <em>certificado</em> y a partir de la reforma tiene que estar{" "}
          <strong>homologado</strong>. Hay un periodo transitorio de un año, así que los
          cascos certificados siguen valiendo hasta el 1 de octubre de 2027.
        </P>

        <H3>Motos por el arcén: sí, pero con muchas condiciones</H3>
        <P>
          Es la novedad más comentada y la peor explicada. El <Art>art. 36.3</Art>{" "}
          <strong>no autoriza a circular por el arcén siempre que haya atasco</strong>.
          Permite habilitar ese uso extraordinario con cuatro condiciones acumulativas:
        </P>
        <UL>
          <LI>
            Solo en <strong>tramos previamente acordados</strong> entre el titular de la
            vía y la Jefatura Central de Tráfico o la autoridad autonómica o local
            competente.
          </LI>
          <LI>
            Esos tramos deben estar <strong>señalizados con señal vertical fija y marcas
            viales</strong>.
          </LI>
          <LI>
            Velocidad limitada a <strong>30 km/h</strong>, en <strong>columna de a uno</strong>{" "}
            y extremando la precaución.
          </LI>
          <LI>
            Solo cuando la circulación de los carriles esté{" "}
            <strong>detenida por congestión del tráfico</strong>.
          </LI>
        </UL>
        <P>
          Además, el motorista debe asegurarse de no poner en riesgo a quienes están
          obligados a circular por el arcén (ciclos, ciclomotores, vehículos para personas
          de movilidad reducida) y abandonarlo si es necesario. En la práctica, esto no
          será utilizable hasta que cada tramo esté acordado y señalizado.
        </P>

        <H3>Chaleco reflectante e intercomunicadores</H3>
        <UL>
          <LI>
            Las <strong>motocicletas deberán llevar en su dotación un chaleco reflectante</strong>{" "}
            de alta visibilidad (modificación del Reglamento General de Vehículos).
          </LI>
          <LI>
            Quienes circulen en moto, ciclomotor, bici o VMP{" "}
            <strong>por motivos profesionales</strong> deberán{" "}
            <strong>usar chaleco reflectante en todo tipo de vías</strong> <Art>art. 118.4</Art>.
          </LI>
          <LI>
            El <Art>art. 18.2</Art> aclara una zona gris de años: se permiten los{" "}
            <strong>dispositivos inalámbricos certificados u homologados integrados en el
            casco</strong>, solo para intercomunicación o navegación y siempre que no
            afecten a la seguridad de la conducción.
          </LI>
        </UL>

        {/* 7 */}
        <H2 id="bicicletas">Bicicletas: casco, centro del carril y 5 metros</H2>
        <P>
          El <Art>art. 118.2</Art> mantiene el casco obligatorio en vías interurbanas y{" "}
          <strong>suprime las exenciones</strong> que existían (rampas ascendentes
          prolongadas, calor extremo). Sigue siendo obligatorio en todo tipo de vías para
          menores de 16 años, y se añade una obligación nueva: quienes circulen en
          bicicleta por motivos profesionales deberán llevar casco también en vías urbanas
          y travesías. De noche o con visibilidad reducida en vía interurbana, elemento
          luminoso o reflectante visible a 150 metros.
        </P>
        <P>
          La reforma añade al Reglamento un <strong>Título VI completo, «Normas de
          circulación específicas en zonas urbanas»</strong>, que hasta ahora no existía.
          En materia de bicicletas <Art>art. 153</Art>:
        </P>
        <UL>
          <LI>
            Sin carril o vía específica, las bicicletas circulan por la calzada y lo harán{" "}
            <strong>preferentemente por el centro del carril</strong> en la medida en que
            su seguridad y la de los demás lo permitan.
          </LI>
          <LI>
            En vías con dos o más carriles por sentido, circularán por el carril derecho,
            salvo para cambiar de dirección o por razones de seguridad.
          </LI>
          <LI>
            Los vehículos a motor deben dejar al menos{" "}
            <strong>5 metros de separación con el ciclista que les preceda en el mismo
            carril</strong>. Es distancia por detrás, no separación lateral.
          </LI>
          <LI>
            Los ciclistas podrán rebasar por la derecha o por la izquierda según convenga
            a su seguridad, y en semáforos y retenciones podrán adelantar a los vehículos
            detenidos para colocarse en la zona de espera adelantada.
          </LI>
          <LI>
            En calles de un solo carril limitadas a 30 km/h o menos, y previa
            señalización, el Ayuntamiento podrá permitir la{" "}
            <strong>circulación de bicicletas en los dos sentidos</strong>.
          </LI>
          <LI>
            <strong>Prohibido circular por las aceras</strong>, con la única excepción de
            que la ordenanza municipal lo permita para menores de 12 años acompañados de
            un adulto que vaya a pie.
          </LI>
        </UL>

        {/* 8 */}
        <H2 id="patinetes">Patinetes eléctricos y VMP: la regulación estatal que faltaba</H2>
        <P>
          Hasta ahora la mayor parte de las reglas dependían de cada ordenanza municipal.
          La reforma fija un marco estatal:
        </P>
        <UL>
          <LI><strong>Edad mínima: 15 años</strong> <Art>art. 155.3</Art>.</LI>
          <LI>
            <strong>Casco obligatorio</strong>, homologado o certificado, correctamente
            abrochado, sin distinción de tipo de vía <Art>art. 118.3</Art>.
          </LI>
          <LI>
            <strong>Prohibida la circulación por aceras</strong>, sin excepciones: la acera
            es espacio reservado a peatones <Art>art. 152.1</Art>.
          </LI>
          <LI>
            <strong>Alumbrado siempre encendido</strong> <Art>art. 155.5</Art>. La
            exigencia durante el día se aplaza al 1 de octubre de 2027.
          </LI>
          <LI>
            De noche o con visibilidad reducida, elemento luminoso o reflectante visible a
            150 metros. Y obligación de señalizar las maniobras con el brazo.
          </LI>
          <LI>
            <strong>Dónde no pueden circular</strong> <Art>art. 38.4</Art>: autopistas,
            autovías, travesías, vías interurbanas y túneles urbanos.{" "}
            <strong>Novedad:</strong> sí pueden usar vías ciclistas, carriles bici, pistas
            bici, sendas ciclables y vías donde esté prohibida la circulación de vehículos
            a motor, aunque sean interurbanas, si la señalización no lo prohíbe. Esto abre
            la puerta a desplazamientos entre municipios.
          </LI>
          <LI>
            Las personas con movilidad reducida que se desplacen en VMP al paso de persona{" "}
            <strong>tienen la consideración de peatones</strong>.
          </LI>
        </UL>

        {/* 9 */}
        <H2 id="peatones">Peatones y ciudad: prioridad, plataforma única y caminos escolares</H2>
        <P>El nuevo Título VI da cobertura estatal a figuras que muchos ayuntamientos ya aplicaban:</P>
        <UL>
          <LI>
            <strong>Zonas o áreas de prioridad residencial</strong> <Art>art. 152.3</Art>,
            con restricciones de acceso, circulación o estacionamiento, régimen de carga y
            descarga y límites de velocidad propios.
          </LI>
          <LI>
            <strong>Zonas con plataforma única de calzada y acera</strong>, donde el peatón
            tiene prioridad de paso en cualquier punto de la calzada.
          </LI>
          <LI>
            <strong>Caminos y rutas escolares seguros</strong> <Art>art. 158</Art>, con
            calmado de tráfico en entornos escolares y también en el entorno de centros
            hospitalarios, de mayores o de personas con discapacidad.
          </LI>
          <LI>
            Restricciones medioambientales y zonas de bajas emisiones <Art>art. 157</Art>.
          </LI>
          <LI>
            El <Art>art. 65</Art> mantiene la prioridad peatonal en pasos, aceras y zonas
            peatonales, cuando un vehículo gira para entrar en otra vía y al subir o bajar
            del transporte público. <strong>Sus infracciones son graves.</strong>
          </LI>
        </UL>

        {/* 10 */}
        <H2 id="cinturon">Cinturón: se acaban las exenciones (esto nos toca a nosotros)</H2>
        <P>
          El <Art>art. 119</Art> recorta las exenciones al uso del cinturón. Hasta el 30 de
          septiembre podían circular sin cinturón en poblado —nunca en autopista, autovía o
          convencional— los conductores de taxi en servicio, los distribuidores de
          mercancías que hacían cargas y descargas sucesivas a corta distancia, los
          servicios de urgencia y las personas que acompañan a un alumno durante el
          aprendizaje de la conducción o las pruebas de aptitud y están a cargo de los
          mandos adicionales.
        </P>
        <P>
          La nueva redacción <strong>solo conserva</strong> la exención para servicios de
          urgencia en poblado, para los pasajeros de ambulancias asistenciales que atienden
          al paciente en ruta, y el supuesto de los taxis con menores de 135 cm en asiento
          trasero.
        </P>
        <Callout tone="navy" title="Nos afecta directamente">
          A partir del 1 de octubre, <strong>los profesores de autoescuela llevamos el
          cinturón puesto durante las clases prácticas</strong>, igual que taxistas y
          repartidores. Nos parece razonable, y en Faro ya lo hemos incorporado a la rutina
          de nuestros vehículos.
        </Callout>
        <P>
          Un cambio relacionado <Art>art. 48.1.b</Art>: cuando en un autobús viajen
          pasajeros de pie o el vehículo no tenga cinturones, la velocidad máxima será de{" "}
          <strong>80 km/h en cualquier tipo de vía fuera de poblado</strong>.
        </P>

        {/* 11 */}
        <H2 id="autocaravanas">Autocaravanas: estacionar sí, acampar no</H2>
        <P>
          El <Art>art. 92.4</Art> delimita por fin el límite entre estacionar y acampar.
          Una autocaravana o vehículo acondicionado como vivienda puede estacionar si:
        </P>
        <UL>
          <LI>
            <strong>No extiende elementos</strong> que desborden el perímetro del vehículo,
            entendido como su proyección en planta: nada de toldos, ventanas batientes o
            patas desplegadas.
          </LI>
          <LI>
            <strong>Descansa solo sobre los neumáticos</strong>, con posibilidad de calzos o
            cuñas de seguridad.
          </LI>
          <LI><strong>No vierte fluidos</strong> procedentes del habitáculo.</LI>
        </UL>

        {/* 12 */}
        <H2 id="bulos">Cuidado con estos cuatro bulos</H2>
        <P>Circulan por redes y por algunos medios. Ninguno está en el BOE.</P>

        <H3>«Hay que dejar 5 metros de separación al adelantar a un ciclista»</H3>
        <P>
          Falso. La separación lateral al adelantar sigue siendo{" "}
          <strong>1,5 metros</strong> <Art>art. 85.4</Art>. Los <strong>5 metros</strong>{" "}
          son la distancia mínima que debe guardar un vehículo a motor con la bicicleta{" "}
          <strong>que circula delante de él en el mismo carril</strong>, en ciudad{" "}
          <Art>art. 153.4</Art>. Son dos reglas distintas.
        </P>

        <H3>«Será obligatorio el casco integral en secundarias y autovías»</H3>
        <P>Falso. El artículo 118.1 exige casco homologado UNECE R22, sin distinguir tipos.</P>

        <H3>«Los triángulos de emergencia desaparecen el 1 de octubre»</H3>
        <P>
          No forma parte de esta reforma. La <strong>baliza V16 conectada</strong> es el
          único medio válido de preseñalización <strong>desde el 1 de enero de 2026</strong>,
          por el Reglamento General de Vehículos. Ya está en vigor.
        </P>

        <H3>«Las motos ya pueden usar el arcén en cualquier atasco»</H3>
        <P>
          Falso: solo en tramos acordados y señalizados, a 30 km/h, en columna de a uno y
          con los carriles detenidos.
        </P>
        <P>
          Y una precisión más: el Real Decreto <strong>no fija cuantías de multa</strong>.
          Las sanciones salen de la Ley de Tráfico. Lo que sí hace el Reglamento es
          calificar expresamente como graves las infracciones de determinados artículos
          (35.3, 36.7, 65.3 y 88.3).
        </P>

        {/* 13 */}
        <H2 id="examen">Qué significa esto si te estás sacando el carné</H2>
        <P>Si estás preparando el teórico, esta reforma te afecta de dos maneras a la vez.</P>
        <P>
          <strong>Primera: cambia el temario.</strong> Cada vez que se modifica el Reglamento
          General de Circulación cambia la materia que puede preguntarse. Y aquí no hablamos
          de un retoque: hablamos de un título nuevo completo, de una definición legal nueva
          y de cambios en adelantamientos, velocidades, uso de carriles, equipamiento y
          prioridades. Todo eso es material examinable.
        </P>
        <P>
          <strong>Segunda: cambia la forma de preguntar.</strong> La DGT lleva desde finales
          de 2025 transformando el examen teórico. Primero llegaron las preguntas sobre el
          nuevo catálogo de señales. Después, los <strong>vídeos de percepción del riesgo</strong>:
          la estructura se mantiene en 30 preguntas con tres opciones, pero una de ellas va
          precedida de un clip de tráfico real que solo puedes ver un número limitado de
          veces antes de que aparezca el enunciado. El tiempo de examen se amplía en función
          de la duración del vídeo.
        </P>
        <Callout tone="red" title="Consecuencia práctica">
          Estudiar con test antiguos es un riesgo. Donde antes bastaba con «1,5 metros»,
          ahora hay que añadir la reducción de velocidad y el cambio completo de carril.
          Donde el patinete era cosa de la ordenanza municipal, ahora hay reglas estatales.
          Revisa la fecha de actualización de los test que estés usando.
        </Callout>

        {/* 14 */}
        <H2 id="fechas">Fechas clave</H2>
        <UL>
          <LI>
            <strong>1 de octubre de 2026.</strong> Entrada en vigor general del Real Decreto
            518/2026.
          </LI>
          <LI>
            <strong>1 de octubre de 2027.</strong> Vencen dos periodos transitorios: fin de
            los cascos certificados en ciclomotores y alumbrado diurno obligatorio en
            vehículos de movilidad personal.
          </LI>
          <LI>
            <strong>Sin fecha cerrada.</strong> Las especificaciones técnicas de los guantes
            de protección, de los asientos adicionales y de los remolques de bicicleta se
            fijarán por orden del Ministerio del Interior. Hasta entonces se usarán guantes
            de las mismas características que los actuales.
          </LI>
        </UL>

        {/* 15 FAQ */}
        <H2 id="faq">Preguntas frecuentes</H2>
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-4">
              <summary className="flex cursor-pointer list-none items-start justify-between gap-4 text-[17px] font-semibold text-slate-900 marker:hidden">
                <span>{f.q}</span>
                <span
                  className="mt-1 shrink-0 text-xl leading-none transition-transform group-open:rotate-45"
                  style={{ color: RED }}
                  aria-hidden="true"
                >
                  +
                </span>
              </summary>
              <p className="mt-3 text-[16px] leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>

        {/* FUENTE */}
        <p className="mt-10 text-sm leading-6 text-slate-500">
          Fuente principal: Real Decreto 518/2026, de 24 de junio, por el que se modifica el
          Reglamento General de Circulación (BOE núm. 155, de 26 de junio de 2026).{" "}
          <a
            href="https://www.boe.es/eli/es/rd/2026/06/24/518"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Consultar en el BOE
          </a>
          .
        </p>
      </div>

      {/* CTA */}
      <section className="mt-14 border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-3xl px-5 py-12">
          <h2 className="text-2xl font-bold" style={{ color: NAVY }}>
            ¿Te preparas el carné en Vigo?
          </h2>
          <p className="mt-4 text-[17px] leading-8 text-slate-700">
            En Autoescuelas Faro llevamos desde 1980 formando conductores en Vigo y su
            comarca, con centros en Plaza América, Coruxo, Nigrán, Panxón y A Cañiza. Todo
            nuestro material de aula está actualizado con la normativa vigente desde el 1 de
            octubre de 2026.
          </p>
          <p className="mt-4 text-[17px] leading-8 text-slate-700">
            Y si lo que necesitas es{" "}
            <Link
              to="/recuperacion-de-puntos-vigo"
              className="font-semibold underline decoration-2 underline-offset-2"
              style={{ color: NAVY }}
            >
              recuperar puntos del carnet
            </Link>{" "}
            o{" "}
            <Link
              to="/curso-sensibilizacion-vigo"
              className="font-semibold underline decoration-2 underline-offset-2"
              style={{ color: NAVY }}
            >
              hacer el curso de sensibilización y reeducación vial
            </Link>
            , somos Centro Autorizado por la DGT.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/#contacto"
              className="rounded-lg px-6 py-3 font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              style={{ backgroundColor: RED }}
            >
              Infórmate sin compromiso
            </Link>
            <Link
              to="/ciclo-adicional-vigo"
              className="rounded-lg border-2 px-6 py-3 font-semibold transition-colors hover:bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              style={{ borderColor: NAVY, color: NAVY }}
            >
              Ciclo adicional de formación
            </Link>
          </div>
        </div>
      </section>
    </article>
  );
}

const EXTRA_META = [
  ["property", "og:type", "article"],
  ["property", "og:title", "Todos los cambios de la DGT que entran en vigor el 1 de octubre de 2026"],
  ["property", "og:description", "Leemos el Real Decreto 518/2026 artículo por artículo y desmontamos los cuatro bulos que circulan."],
  ["property", "article:published_time", PUBLISHED],
  ["property", "article:section", "Normativa"],
  ["name", "robots", "index, follow"],
];

export default function CambiosDGTOctubre2026() {
  useSeo({
    title: "Cambios de la DGT del 1 de octubre de 2026: guía completa",
    description: "El Real Decreto 518/2026 modifica el Reglamento de Circulación desde el 1 de octubre: adelantar a ciclistas, motos en el arcén, casco en patinetes, cinturón y más.",
    canonical: URL,
    ogImage: "/assets/blog/cambios-dgt-octubre-2026.webp",
    ogImageAlt: "Nuevas normas de circulación de la DGT que entran en vigor el 1 de octubre de 2026",
    extraMeta: EXTRA_META,
  });
  return (
    <div className="pb-[calc(env(safe-area-inset-bottom)+72px)] sm:pb-0" data-testid="cambios-dgt-page">
      <Header />
      <CambiosDGTArticle />
      <Footer />
      <FloatingWhatsapp />
      <MobileBottomNav />
    </div>
  );
}
